import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ChevronLeft, Shield, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  {
    id: "section_1",
    title: "Membership Estimator",
    subtitle: "Section 1 of 3",
    description: "Estimate your required DISP membership tier based on clearance levels and contract status.",
    questions: [
      {
        id: "qs_personnel_clearance",
        label: "What is the highest personnel security clearance held by staff in your organisation?",
        options: [
          { value: "none", label: "None" },
          { value: "baseline", label: "Baseline" },
          { value: "nv1", label: "Negative Vetting Level 1 (NV1)" },
          { value: "nv2", label: "Negative Vetting Level 2 (NV2)" },
        ],
      },
      {
        id: "qs_physical_level",
        label: "What is the highest physical security zone required at your premises?",
        options: [
          { value: "none", label: "None" },
          { value: "official_protected", label: "Official / Protected" },
          { value: "secret", label: "Secret" },
          { value: "top_secret", label: "Top Secret" },
        ],
      },
      {
        id: "qs_ict_level",
        label: "What is the highest ICT system classification your organisation handles?",
        options: [
          { value: "none", label: "None" },
          { value: "official_protected", label: "Official / Protected" },
          { value: "secret", label: "Secret" },
          { value: "top_secret", label: "Top Secret" },
        ],
      },
      {
        id: "qs_contract_status",
        label: "What is your current Defence contract status?",
        options: [
          { value: "current", label: "Currently holding a Defence contract" },
          { value: "bidding", label: "Actively bidding on a Defence contract" },
          { value: "unsure", label: "Unsure / exploring options" },
        ],
      },
    ],
  },
  {
    id: "section_2",
    title: "Cyber & Governance",
    subtitle: "Section 2 of 3",
    description: "Rate your current cyber security and governance maturity across six control domains.",
    questions: [
      {
        id: "qs_gov_policy_score",
        label: "Does your organisation have a security governance policy?",
        options: [
          { value: "0", label: "No" },
          { value: "1", label: "Informal" },
          { value: "2", label: "Implemented" },
          { value: "3", label: "Documented & Reviewed" },
        ],
      },
      {
        id: "qs_mfa_score",
        label: "Is multi-factor authentication (MFA) enforced across critical systems?",
        options: [
          { value: "0", label: "No" },
          { value: "1", label: "Informal" },
          { value: "2", label: "Implemented" },
          { value: "3", label: "Documented & Reviewed" },
        ],
      },
      {
        id: "qs_incident_score",
        label: "Does your organisation have a cyber incident response plan?",
        options: [
          { value: "0", label: "No" },
          { value: "1", label: "Informal" },
          { value: "2", label: "Implemented" },
          { value: "3", label: "Documented & Reviewed" },
        ],
      },
      {
        id: "qs_framework_score",
        label: "Is your organisation aligned to a recognised cyber security framework (e.g., Essential Eight, ISO 27001)?",
        options: [
          { value: "0", label: "No" },
          { value: "1", label: "Informal" },
          { value: "2", label: "Implemented" },
          { value: "3", label: "Documented & Reviewed" },
        ],
      },
      {
        id: "qs_training_score",
        label: "Do staff receive regular security awareness training?",
        options: [
          { value: "0", label: "No" },
          { value: "1", label: "Informal" },
          { value: "2", label: "Implemented" },
          { value: "3", label: "Documented & Reviewed" },
        ],
      },
      {
        id: "qs_independent_assess_score",
        label: "Has your organisation undergone an independent security assessment or audit?",
        options: [
          { value: "0", label: "No" },
          { value: "1", label: "Informal" },
          { value: "2", label: "Implemented" },
          { value: "3", label: "Documented & Reviewed" },
        ],
      },
    ],
  },
  {
    id: "section_3",
    title: "FOCI Quick Scan",
    subtitle: "Section 3 of 3",
    description: "Foreign Ownership, Control or Influence indicators relevant to DISP eligibility.",
    questions: [
      {
        id: "qs_foreign_ownership",
        label: "What percentage of your organisation is owned by foreign entities?",
        options: [
          { value: "0", label: "0% – Fully Australian owned" },
          { value: "under_20", label: "Under 20%" },
          { value: "between_20_39", label: "Between 20% and 39%" },
          { value: "40_plus", label: "40% or more" },
        ],
      },
      {
        id: "qs_foreign_board",
        label: "Are any members of your board or executive leadership foreign nationals?",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
      {
        id: "qs_foreign_revenue",
        label: "Does your organisation derive significant revenue from foreign government contracts?",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
];

function RadioCard({ option, selected, onSelect, questionId }) {
  return (
    <button
      type="button"
      data-testid={`option-${questionId}-${option.value}`}
      onClick={() => onSelect(option.value)}
      className={`w-full text-left px-4 py-3 rounded-sm border transition-all duration-150 flex items-center gap-3
        ${selected
          ? "border-[#c9a84c] bg-[#c9a84c]/10 text-[#f1f5f9]"
          : "border-[#1e3a5f] bg-[#112d4e]/50 text-[#94a3b8] hover:border-[#c9a84c]/40 hover:text-[#f1f5f9]"
        }`}
    >
      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors
        ${selected ? "border-[#c9a84c]" : "border-[#334155]"}`}>
        {selected && <span className="w-2 h-2 rounded-full bg-[#c9a84c]" />}
      </span>
      <span className="text-sm font-medium">{option.label}</span>
    </button>
  );
}

export default function Assessment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const currentSection = SECTIONS[step];
  const progress = Math.round(((step) / SECTIONS.length) * 100);

  const setAnswer = (qId, value) =>
    setAnswers((prev) => ({ ...prev, [qId]: value }));

  const currentAnswered = currentSection.questions.filter(
    (q) => answers[q.id] !== undefined
  ).length;
  const allCurrentAnswered = currentAnswered === currentSection.questions.length;

  const handleNext = () => {
    if (!allCurrentAnswered) {
      setError("Please answer all questions before continuing.");
      return;
    }
    setError("");
    setStep((s) => s + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setError("");
    setStep((s) => s - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!allCurrentAnswered) {
      setError("Please answer all questions before submitting.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/assessment/DISP_QS_V1/calculate`,
        { answers }
      );
      navigate("/results", { state: { results: data, answers } });
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1f33] text-[#f1f5f9] font-['Inter',sans-serif] py-8 px-4">
      {/* Progress header */}
      <div className="max-w-2xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-[#c9a84c]" />
            <span className="font-['Manrope',sans-serif] font-bold text-sm text-[#c9a84c] tracking-widest uppercase">
              DefencIQ™
            </span>
          </div>
          <span className="text-[#94a3b8] text-sm font-mono" data-testid="progress-label">
            {step + 1} / {SECTIONS.length}
          </span>
        </div>
        <div className="h-1.5 bg-[#1e3a5f] rounded-full overflow-hidden" data-testid="progress-bar">
          <div
            className="h-full bg-[#c9a84c] rounded-full transition-all duration-500"
            style={{ width: `${progress + 33}%` }}
          />
        </div>
        <p className="text-[#94a3b8] text-xs mt-2 flex items-center gap-1">
          <span>Estimated time remaining:</span>
          <span className="font-mono">{Math.max(1, 3 - step)} min</span>
        </p>
      </div>

      {/* Section card */}
      <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-400">
        <div className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm p-6 mb-4">
          <p className="text-[#c9a84c] text-xs font-['Manrope',sans-serif] font-semibold tracking-widest uppercase mb-1">
            {currentSection.subtitle}
          </p>
          <h2 className="font-['Manrope',sans-serif] font-bold text-2xl text-[#f1f5f9] mb-2"
            data-testid="section-title">
            {currentSection.title}
          </h2>
          <p className="text-[#94a3b8] text-sm">{currentSection.description}</p>
        </div>

        {/* Questions */}
        <div className="space-y-6">
          {currentSection.questions.map((q, qi) => (
            <div
              key={q.id}
              className="bg-[#112d4e] border border-[#1e3a5f] rounded-sm p-5"
              data-testid={`question-${q.id}`}
            >
              <p className="text-[#f1f5f9] text-sm font-medium mb-3 leading-relaxed">
                <span className="text-[#c9a84c] font-mono font-bold mr-2">
                  Q{qi + 1}.
                </span>
                {q.label}
              </p>
              <div className="space-y-2">
                {q.options.map((opt) => (
                  <RadioCard
                    key={opt.value}
                    option={opt}
                    questionId={q.id}
                    selected={answers[q.id] === opt.value}
                    onSelect={(val) => setAnswer(q.id, val)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mt-4 px-4 py-3 bg-red-900/20 border border-red-500/30 rounded-sm text-red-400 text-sm"
            data-testid="assessment-error">
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 mb-12">
          <button
            data-testid="back-btn"
            onClick={handleBack}
            disabled={step === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#1e3a5f] rounded-sm text-sm text-[#94a3b8] hover:text-[#f1f5f9] hover:border-[#c9a84c]/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={15} />
            Back
          </button>

          {step < SECTIONS.length - 1 ? (
            <button
              data-testid="next-btn"
              onClick={handleNext}
              className="inline-flex items-center gap-2 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-wider px-6 py-2.5 rounded-sm hover:bg-[#d4b460] transition-all shadow-[0_0_16px_rgba(201,168,76,0.25)]"
            >
              Next Section
              <ChevronRight size={15} />
            </button>
          ) : (
            <button
              data-testid="submit-btn"
              onClick={handleSubmit}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-[#c9a84c] text-[#0a1f33] font-['Manrope',sans-serif] font-bold text-sm uppercase tracking-wider px-6 py-2.5 rounded-sm hover:bg-[#d4b460] transition-all disabled:opacity-70 shadow-[0_0_16px_rgba(201,168,76,0.25)]"
            >
              {loading ? (
                <><Loader2 size={15} className="animate-spin" /> Calculating…</>
              ) : (
                <>View My Results <ChevronRight size={15} /></>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
